import React, { useEffect, useState } from "react";
import CalendarService from "service/CalendarService";
import { useSelector } from "react-redux";
import { CalendarDateResp } from "domain/payload/response/Calendar/CalendarDateResp";
import CustomDropdown, { DropdownOption } from "component/CustomDropdown";


const Calendar: React.FC = () => {
  const today = new Date();
  const getDateKey = (date: Date) => date.toDateString();

  // USESTATE
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [dataEvents, setDataEvents] = useState<CalendarDateResp[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [formMonth, setFormMonth] = useState(month);
  const [formYear, setFormYear] = useState(year);

  const { eventList } = CalendarService();
  const holidaySet = new Set<string>();
  const collectiveLeaveSet = new Set<string>();

  const { profile } = useSelector((state: any) => state.auth);
  const daysName = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"]
  const years = Array.from({ length: 2038 - 1970 + 1 }, (_, i) => 1970 + i);

  // months name
  const months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(year, i, 1);

    return {
      idx: i,
      long: date.toLocaleDateString("id-ID", { month: "long" }),
      short: date.toLocaleDateString("id-ID", { month: "short" })
    };
  });

  const monthOptions: DropdownOption[] = months.map((monthName) => ({
    id: monthName.idx,
    name: monthName.long,
  }));

  const yearOptions: DropdownOption[] = years.map((y) => ({
    id: y,
    name: String(y),
  }));

  // Fetch data
  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const res = await eventList(token, year, month + 1);
      setDataEvents(res.data ?? []);
    } catch (error: any) {
      console.error("Error fetching calendar events:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Terjadi kesalahan saat mengambil data kalender';
      window.alert(`Error: ${errorMessage}`);
    }
  }

  useEffect(() => {
    fetchData();
  }, [month, year]);

  // prev and next month
  const changeMonth = (offset: number) => {
    let newMonth = month + offset;
    let newYear = year;

    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }

    setMonth(newMonth);
    setYear(newYear);
    setFormMonth(newMonth);
    setFormYear(newYear);
  };

  //cek event
  dataEvents.forEach(dateResp => {
    const key = getDateKey(new Date(dateResp.date));

    dateResp.events.forEach(ev => {
      if (ev.type === "national_holiday") {
        holidaySet.add(key);
      }

      if (ev.type === "collective_leave") {
        collectiveLeaveSet.add(key);
      }
    });
  });

  // calendar
  const calendarBuild = () => {
    const firstDay = new Date(year, month, 1)
    const start = new Date(firstDay)
    start.setDate(firstDay.getDate() - firstDay.getDay())

    const weeks = []
    let current = new Date(start)

    for (let w = 0; w < 6; w++) {
      const week = []
      for (let d = 0; d < 7; d++) {
        const key = getDateKey(current);
        week.push({
          date: new Date(current),
          day: current.getDate(),
          isToday: current.toDateString() === today.toDateString(),
          isCurrentMonth: current.getMonth() === month,
          isWeekend: current.getDay() === 0 || current.getDay() === 6,
          isHoliday: holidaySet.has(key),
          isCollectiveLeave: collectiveLeaveSet.has(key),
        })

        current.setDate(current.getDate() + 1)
      }
      weeks.push(week)
    }
    return weeks
  }
  const calendar = calendarBuild()

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Calendar</h5>
          </div>
        </div>
      </div>

      <div className="px-3 px-md-5 py-4 my-1 card" style={{ background: 'white' }}>
        <div className="card-body">
          <div className="gap-2 mb-4 d-flex align-items-center">
            <button className="p-0 bg-white btn btn-outline-primary rounded-3 btn-small" onClick={() => changeMonth(-1)}><i className="fa-solid fa-chevron-left" /></button>
            <div className="position-relative d-inline-block">
              <button className="px-3 py-0 btn btn-outline-primary" style={{ height: 30, width: 140 }} onClick={() => setShowFilter(!showFilter)}>
                {months[month].long} {year}
              </button>
              {showFilter && (
                <>
                  <div className="d-block d-sm-none">
                    <div className="modal fade show" aria-modal="true" style={{ display: 'block', zIndex: 1055 }} onClick={() => setShowFilter(false)} >
                      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                          <div className="modal-body">

                            <div className="mb-3 row g-2">
                              <div className="col-12 col-sm-6">
                                <CustomDropdown
                                  value={formMonth}
                                  options={monthOptions}
                                  onChange={(opt) => setFormMonth(Number(opt.id))}
                                  containerClassName="w-100"
                                  buttonClassName="form-select w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark"
                                />
                              </div>

                              <div className="col-12 col-sm-6">
                                <CustomDropdown
                                  value={formYear}
                                  options={yearOptions}
                                  onChange={(opt) => setFormYear(Number(opt.id))}
                                  containerClassName="w-100"
                                  buttonClassName="form-select w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark"
                                />
                              </div>
                            </div>

                            <div className="gap-2 d-flex">
                              <button
                                className="p-0 mb-2 text-white border-0 rounded-2 btn-primary"
                                style={{ height: 30, width: 30 }}
                                onClick={() => {
                                  setMonth(today.getMonth());
                                  setYear(today.getFullYear());
                                  setFormMonth(today.getMonth());
                                  setFormYear(today.getFullYear());
                                  setShowFilter(false);
                                }}
                              >
                                <i className="fa-solid fa-calendar-day"></i>
                              </button>

                              <button
                                className="p-0 btn btn-primary w-100"
                                style={{ height: 30 }}
                                onClick={() => {
                                  setMonth(formMonth);
                                  setYear(formYear);
                                  setShowFilter(false);
                                }}
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} onClick={() => setShowFilter(false)} />
                  </div>

                  <div className="d-none d-sm-block">
                    <div className="top-0 position-fixed start-0 w-100 h-100" style={{ zIndex: 999 }} onClick={() => setShowFilter(false)} />
                    <div className="p-3 mt-2 shadow card position-absolute animate-pop-in" style={{ width: "300px", zIndex: 1000 }}>

                      <div className="mb-3 row g-2">
                        <div className="col-12 col-sm-6">
                          <CustomDropdown
                            value={formMonth}
                            options={monthOptions}
                            onChange={(opt) => setFormMonth(Number(opt.id))}
                            containerClassName="w-100"
                          />
                        </div>

                        <div className="col-12 col-sm-6">
                          <CustomDropdown
                            value={formYear}
                            options={yearOptions}
                            onChange={(opt) => setFormYear(Number(opt.id))}
                            containerClassName="w-100"
                          />
                        </div>
                      </div>

                      <div className="gap-2 d-flex">
                        <button className="p-0 mb-2 text-white border-0 rounded-2 btn-primary" style={{ height: 30, width: 30 }}
                          onClick={() => {
                            setMonth(today.getMonth());
                            setYear(today.getFullYear());
                            setFormMonth(today.getMonth());
                            setFormYear(today.getFullYear());
                            setShowFilter(false);
                          }}><i className="fa-solid fa-calendar-day"></i>
                        </button>

                        <button className="p-0 btn btn-primary w-100" style={{ height: 30 }}
                          onClick={() => {
                            setMonth(formMonth);
                            setYear(formYear);
                            setShowFilter(false);
                          }}>Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <button className="p-0 bg-white btn btn-outline-primary rounded-3 btn-small" onClick={() => changeMonth(1)}><i className="fa-solid fa-chevron-right" /></button>
          </div>

          {/* Calendar */}
          <div className="gap-3 row">
            <div className="col">
              <div className="mb-2 row fw-semibold">
                {daysName.map((dayName) => (
                  <div key={dayName} className="p-1 col text-end text-truncate">
                    {dayName}
                  </div>
                ))}
              </div>

              {calendar.map((week, idx) => (
                <div className="row" key={idx}>
                  {week.map((day, idx) => {
                    return (
                      <div
                        key={idx}
                        className={`d-flex justify-content-end gap-1 col border p-2 text-end overflow-hidden ${(day.isToday || (day.isToday && !day.isCurrentMonth)) ? "text-white" : "calendar-hover"}`}
                        style={{
                          height: 80,
                          color: day.isToday ? "#ffffff" : day.isWeekend || day.isHoliday ? "red" : "#2b75da",
                          backgroundColor: day.isToday ? "#2b75da" : "#f8f9fa",
                          fontWeight: 600,
                          opacity: day.isCurrentMonth ? 1 : 0.3
                        }}
                      >
                        {day.isCollectiveLeave && (<div style={{ color: "#FF0000" }}>●</div>)}
                        {day.day}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Events */}
            <div className="pl-3 col-md-3 border-start">
              <h6 className="my-2 fw-semibold">Events</h6>
              <div style={{ maxHeight: '450px', overflowY: 'auto', paddingRight: '8px' }}>
                {dataEvents.length === 0 && (
                  <div className="p-3 text-center text-muted card">
                    No events found
                  </div>
                )}
                {dataEvents.map((dateResp) => {
                  const eventDate = new Date(dateResp.date);

                  return dateResp.events.map((ev) => (
                    <div key={ev.id} className="gap-3 p-2 mb-3 border rounded d-flex align-items-center border-primary">
                      <div className="text-center" style={{ width: 40, color: `${ev.type === "national_holiday" || ev.type === "collective_leave" ? "#FF0000" : "#2b75da"}` }}>
                        <div style={{ fontSize: 22, lineHeight: 1 }} className="fw-semibold">{eventDate.getDate()}</div>
                        <small>{months[eventDate.getMonth()].short}</small>
                      </div>
                      <div>
                        <div className="text-primary fw-semibold">
                          {ev.title}
                        </div>
                        <small className="text-muted fst-italic">
                          {ev.type === "national_holiday" ? "National Holiday" : ev.type === "collective_leave" ? "Collective Leave" : ev.type}
                        </small>
                      </div>
                    </div>
                  ));
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;